import { Button } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import { useSaveCartAsList } from "@/queries/customer/saved-lists";
import { toast } from "@medusajs/ui";
import { BookmarkPlus } from "lucide-react";
import { memo, useCallback, useState } from "react";

import { SavedListNameDialog } from "../saved-lists/SavedListNameDialog";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

/** A friendly date-based default like "Restock - 21 Jul". */
function defaultListName(now: Date = new Date()): string {
  return `Restock - ${now.getDate()} ${MONTHS[now.getMonth()]}`;
}

interface SaveCartAsListButtonProps {
  /** Disabled when there are no items across the active carts. */
  disabled?: boolean;
  className?: string;
}

/**
 * "Save current cart as a list" (F3, #60). Snapshots the active seller carts
 * into a new saved list via `useSaveCartAsList` - **non-destructive**, the cart
 * is untouched. Disabled when every cart is empty.
 */
export const SaveCartAsListButton = memo(function SaveCartAsListButton({
  disabled = false,
  className
}: SaveCartAsListButtonProps) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveCart = useSaveCartAsList();

  const handleSubmit = useCallback(
    async (name: string) => {
      setError(null);
      try {
        const { saved_list } = await saveCart.mutateAsync({ name });
        analytics.savedListCreated({
          list_id: saved_list.id,
          source: "from_cart",
          item_count: saved_list.items.length
        });
        setOpen(false);
        toast.success("Saved to your lists", {
          description: `"${saved_list.name}" - your cart was not changed.`
        });
      } catch (err) {
        setError(
          handleActionError(err, {
            boundary: "save cart as list",
            fallback: "Could not save your list. Please try again."
          })
        );
      }
    },
    [saveCart]
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={className}
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <BookmarkPlus className="mr-2 h-4 w-4" aria-hidden="true" />
        Save as list
      </Button>

      <SavedListNameDialog
        open={open}
        onOpenChange={setOpen}
        title="Save cart as a list"
        description="Save these items as a reusable list. Your cart won't change."
        submitLabel="Save list"
        defaultName={defaultListName()}
        isSubmitting={saveCart.isPending}
        error={error}
        onSubmit={handleSubmit}
      />
    </>
  );
});
